import React, { useRef, useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import Container from '../components/Container';
import StructuralCard from './StructuralCard';

/*
 * §7.2.2 — horizontal card slider (3 visible + peek). Comfortable, professional
 * navigation: click-and-drag to scroll (grab cursor), smooth one-card-per-wheel
 * stepping (with cooldown + release to page scroll at the edges), prev/next
 * arrows, and dots. Native scroll-snap (proximity) keeps it touch-friendly.
 */
export default function StructuralSlider({ cards }) {
  const viewportRef = useRef(null);
  const [active, setActive] = useState(0);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const [dragging, setDragging] = useState(false);

  const drag = useRef({ active: false, startX: 0, startScroll: 0, moved: 0 });
  const wheelLock = useRef(false);

  const reduce = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const stepWidth = () => {
    const vp = viewportRef.current;
    const card = vp && vp.querySelector('.scard');
    return card ? card.getBoundingClientRect().width + 24 : 0;
  };

  const update = useCallback(() => {
    const vp = viewportRef.current;
    const step = stepWidth();
    if (!vp || !step) return;
    setActive(Math.round(vp.scrollLeft / step));
    setAtStart(vp.scrollLeft <= 2);
    setAtEnd(vp.scrollLeft + vp.clientWidth >= vp.scrollWidth - 2);
  }, []);

  const scrollByCards = useCallback((dir) => {
    const vp = viewportRef.current;
    const step = stepWidth();
    if (!vp || !step) return;
    vp.scrollBy({ left: dir * step, behavior: reduce() ? 'auto' : 'smooth' });
  }, []);

  const goTo = (i) => {
    const vp = viewportRef.current;
    const step = stepWidth();
    if (!vp || !step) return;
    vp.scrollTo({ left: i * step, behavior: reduce() ? 'auto' : 'smooth' });
  };

  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return undefined;
    update();
    vp.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);

    // Wheel → one card per gesture (smooth); release to the page at the edges.
    const onWheel = (e) => {
      const delta = Math.abs(e.deltaY) >= Math.abs(e.deltaX) ? e.deltaY : e.deltaX;
      if (Math.abs(delta) < 6) return;
      const fwd = delta > 0;
      const sAtStart = vp.scrollLeft <= 1;
      const sAtEnd = vp.scrollLeft + vp.clientWidth >= vp.scrollWidth - 1;
      if ((fwd && sAtEnd) || (!fwd && sAtStart)) return;
      e.preventDefault();
      if (wheelLock.current) return;
      wheelLock.current = true;
      setTimeout(() => {
        wheelLock.current = false;
      }, 420);
      scrollByCards(fwd ? 1 : -1);
    };
    vp.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      vp.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      vp.removeEventListener('wheel', onWheel);
    };
  }, [update, scrollByCards]);

  // Click-and-drag to scroll (mouse/pen; touch scrolls natively).
  const onPointerDown = (e) => {
    if (e.pointerType === 'touch') return;
    const vp = viewportRef.current;
    if (!vp) return;
    drag.current = { active: true, startX: e.clientX, startScroll: vp.scrollLeft, moved: 0 };
    setDragging(true);
    vp.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!drag.current.active) return;
    const vp = viewportRef.current;
    if (!vp) return;
    const dx = e.clientX - drag.current.startX;
    drag.current.moved = Math.max(drag.current.moved, Math.abs(dx));
    vp.scrollLeft = drag.current.startScroll - dx;
  };
  const endDrag = (e) => {
    if (!drag.current.active) return;
    drag.current.active = false;
    setDragging(false);
    const vp = viewportRef.current;
    vp?.releasePointerCapture?.(e.pointerId);
    const step = stepWidth();
    if (vp && step) {
      vp.scrollTo({ left: Math.round(vp.scrollLeft / step) * step, behavior: reduce() ? 'auto' : 'smooth' });
    }
  };
  // Suppress card link clicks that are really the end of a drag.
  const onClickCapture = (e) => {
    if (drag.current.moved > 6) {
      e.preventDefault();
      e.stopPropagation();
      drag.current.moved = 0;
    }
  };

  if (!cards || !cards.length) return null;

  return (
    <div className="struct-slider">
      <Container>
        <div className="slider-stage">
          <button
            className="slider-arrow slider-arrow--prev"
            onClick={() => scrollByCards(-1)}
            disabled={atStart}
            aria-label="Previous cards"
          >
            <ChevronLeft size={22} />
          </button>

          <div
            className={`struct-slider__viewport${dragging ? ' is-dragging' : ''}`}
            ref={viewportRef}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            onClickCapture={onClickCapture}
          >
            {cards.map((card) => (
              <StructuralCard key={card.id} card={card} />
            ))}
          </div>

          <button
            className="slider-arrow slider-arrow--next"
            onClick={() => scrollByCards(1)}
            disabled={atEnd}
            aria-label="Next cards"
          >
            <ChevronRight size={22} />
          </button>
        </div>

        <div className="slider-dots" role="tablist" aria-label="Slide navigation">
          {cards.map((card, i) => (
            <button
              key={card.id}
              className={`slider-dot${i === active ? ' is-active' : ''}`}
              onClick={() => goTo(i)}
              aria-label={`Go to card ${i + 1}`}
              aria-selected={i === active}
              role="tab"
            />
          ))}
        </div>
      </Container>
    </div>
  );
}
